var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, styled } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ToastContainer, Zoom, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { TaskStatus, TaskType } from '../hooks';
import { useToastMessagesAPI, useUpdateStatusAPI } from './selector';
var LIMIT = 5;
var DELAY = 1500;
var AUTO_HIDE_DURATION = LIMIT * DELAY;
var INTERVAL_CHECK_ASYNC_PROCESS = 10000;
var PENDING_STATUSES = new Set([TaskStatus.Created, TaskStatus.Processing]);
var StyledToastContainer = styled(ToastContainer)(function (_a) {
  var theme = _a.theme;
  return {
    width: 'auto',
    '& .Toastify__toast': {
      width: 'fit-content',
      maxWidth: '90vw'
    },
    '& .Toastify__toast-body': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      whiteSpace: 'nowrap'
    },
    '& .Toastify__toast-theme--colored.Toastify__toast--success': {
      backgroundColor: theme.palette.success.main
    },
    '& .Toastify__toast-theme--colored.Toastify__toast--error': {
      backgroundColor: theme.palette.error.main
    }
  };
});
var StyledLink = styled(Link)(function (_a) {
  var theme = _a.theme;
  return {
    flexShrink: 0,
    color: theme.palette.common.white,
    textDecorationColor: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
    whiteSpace: 'nowrap',
    '&:hover': {
      color: theme.palette.common.white
    }
  };
});
export var ToastMessage = React.memo(function (props) {
  var t = useTranslation('translation', {
    keyPrefix: 'toastify'
  }).t;
  var _a = props.message,
    id = _a.id,
    status = _a.status,
    taskType = _a.taskType,
    details = _a.details,
    scenarioName = _a.scenarioName;
  var _b = useUpdateStatusAPI(id),
    fetchUpdateStatusData = _b[1];
  useEffect(function () {
    return function () {
      fetchUpdateStatusData({
        isNotified: true
      });
    };
  }, [fetchUpdateStatusData, props.message]);
  return _jsxs(Box, __assign({
    component: "span",
    sx: {
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }
  }, {
    children: [t(status, {
      context: taskType,
      planName: taskType === TaskType.ActualReportExport ? details.scenarioName : details.planName,
      scenarioName: scenarioName,
      defaultValue: ''
    }), status === TaskStatus.Completed && taskType === TaskType.MultiskillSimulation && _jsx(StyledLink, __assign({
      to: "/report/".concat(details.basePlanId, "/resourcing/summary")
    }, {
      children: t('viewResults')
    }))]
  }));
});
export var Toastify = function () {
  var _a = useToastMessagesAPI(),
    toastMessagesData = _a[0],
    fetchToastMessagesData = _a[1],
    clearToastMessagesData = _a[2];
  var toastStatusRef = useRef({});
  useEffect(function () {
    fetchToastMessagesData();
    return clearToastMessagesData;
  }, [fetchToastMessagesData, clearToastMessagesData]);
  useEffect(function () {
    var _a;
    var isPollingNeeded = (_a = toastMessagesData.data) === null || _a === void 0 ? void 0 : _a.some(function (_a) {
      var status = _a.status;
      return PENDING_STATUSES.has(status);
    });
    if (!isPollingNeeded) return;
    var timeoutId = setTimeout(fetchToastMessagesData, INTERVAL_CHECK_ASYNC_PROCESS);
    return function () {
      return clearTimeout(timeoutId);
    };
  }, [fetchToastMessagesData, toastMessagesData.data]);
  useEffect(function () {
    var _a;
    var toastStatus = {};
    (_a = toastMessagesData.data) === null || _a === void 0 ? void 0 : _a.filter(function (message) {
      return !PENDING_STATUSES.has(message.status);
    }).forEach(function (message, index) {
      toastStatus[message.id] = true;
      if (message.details.downloadUrl && message.status === TaskStatus.Completed && message.taskType === TaskType.ActualReportExport) {
        var link = document.createElement('a');
        link.href = message.details.downloadUrl;
        link.click();
      }
      !toastStatusRef.current[message.id] && setTimeout(function () {
        toast(_jsx(ToastMessage, {
          message: message
        }), {
          type: message.status === TaskStatus.Completed ? 'success' : 'error',
          theme: 'colored'
        });
      }, index * DELAY);
    });
    toastStatusRef.current = toastStatus;
  }, [toastMessagesData.data]);
  return _jsx(StyledToastContainer, {
    hideProgressBar: true,
    autoClose: AUTO_HIDE_DURATION,
    transition: Zoom,
    limit: LIMIT,
    closeOnClick: true,
    position: "top-center"
  });
};